import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ErrorState, PageSpinner } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { cn, formatDate } from '@/lib/utils'
import type { BillingPeriod, PlanResponse } from '@/types/api'

import {
  CheckIcon,
  CrossIcon,
  PeriodToggle,
  PlanCard,
  RECOMMENDED_PLAN,
  activePlanKey,
  featureRows,
  formatCount,
  planAction,
} from './planUi'
import { useCouponStore } from './coupon'
import { CouponField } from './CouponField'
import { hasPaidPlan, useCheckout, usePlans, useSubscription } from './useLicense'

/**
 * Pricing: three plans, a billing-period switch, the current plan marked,
 * and a feature comparison underneath. Choosing sends the user to Stripe
 * Checkout, or moves an existing paid subscription to the new plan.
 */
export function PlansPage() {
  const plansQuery = usePlans()
  const subscriptionQuery = useSubscription()
  const checkout = useCheckout()
  const subscription = subscriptionQuery.data
  const [period, setPeriod] = useState<BillingPeriod>(() => subscription?.subscription?.period ?? 'monthly')
  const coupon = useCouponStore((state) => state.applied)
  const applyStored = useCouponStore((state) => state.applyStored)

  // A code from a `?coupon=` link: fill it in and check it once.
  useEffect(() => applyStored(), [applyStored])

  if (plansQuery.isLoading) return <PageSpinner label="Loading plans" />
  const data = plansQuery.data
  if (!data) return <ErrorState title="Plans unavailable" description="Try again in a moment." onRetry={() => plansQuery.refetch()} />

  const current = activePlanKey(subscription)
  const sub = subscription?.subscription
  const paidPlan = hasPaidPlan(subscription)

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Plans"
        title="Choose a plan for your next move"
        description="Every plan resets its allowance each month of the subscription. Your profile, job matches and the assistant stay free without a plan."
      />

      {current && sub && (
        <p className="panel flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 text-[13.5px] text-ink-soft">
          You are on <strong className="font-semibold text-ink">{sub.planName}</strong>
          <span className="text-ink-faint">·</span>
          {sub.cancelAtPeriodEnd ? `ends ${formatDate(sub.currentPeriodEnd)}` : sub.renewsAt ? `renews ${formatDate(sub.renewsAt)}` : `until ${formatDate(sub.currentPeriodEnd)}`}
          <Link to="/dashboard/profile/subscription" className="font-medium text-ink underline underline-offset-2 sm:ml-auto">
            Manage subscription
          </Link>
        </p>
      )}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col items-start gap-2">
          <PeriodToggle data={data} value={period} onChange={setPeriod} className="w-full" />
          {!data.billingEnabled && (
            <p className="text-[13px] font-medium text-warning" role="status">
              Payments open soon. You can look around in the meantime.
            </p>
          )}
        </div>
        <CouponField paidPlan={paidPlan} defaultOpen className="panel w-full p-3 lg:max-w-[440px]" />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {data.plans.map((plan) => (
          <PlanCard
            key={plan.key}
            plan={plan}
            period={period}
            current={current === plan.key}
            recommended={plan.key === RECOMMENDED_PLAN}
            action={planAction({ plan, period, subscription, billingEnabled: data.billingEnabled })}
            pending={checkout.pendingPlan === plan.key}
            onChoose={() => checkout.choose(plan.key, period)}
            footnote={!data.billingEnabled ? 'Payments open soon' : undefined}
            coupon={paidPlan ? null : coupon}
          />
        ))}
      </div>

      <Comparison plans={data.plans} />

      <p className="pb-2 text-[12.5px] leading-relaxed text-ink-faint">
        Prices in US dollars. Payments are handled by Stripe; change plan or cancel at any time from Profile →
        Subscription. Unused applications do not roll over.
      </p>
    </div>
  )
}

/** Feature by feature, the three plans side by side. */
function Comparison({ plans }: { plans: PlanResponse[] }) {
  const rows = plans.map((plan) => featureRows(plan.features))
  const labels = (rows[0] ?? []).map((row, index) => {
    // The cover letter row reads differently per plan; the label names the topic.
    if (row.key === 'coverLetter') return { key: row.key, label: 'Cover letter', index }
    return { key: row.key, label: row.label, index }
  })
  return (
    <section className="card overflow-hidden" aria-label="Compare plans">
      <header className="border-b border-line px-4 py-3.5 sm:px-5">
        <h2 className="text-[16px] font-semibold text-ink">Compare plans</h2>
      </header>
      <table className="w-full table-fixed text-left text-[13px]">
        <thead>
          <tr className="border-b border-line text-ink-muted">
            <th scope="col" className="w-[40%] px-4 py-2.5 font-medium sm:px-5">
              <span className="sr-only">Feature</span>
            </th>
            {plans.map((plan) => (
              <th
                key={plan.key}
                scope="col"
                className={cn('px-2 py-2.5 text-center font-semibold text-ink', plan.key === RECOMMENDED_PLAN && 'bg-brand-soft/50')}
              >
                {plan.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          <tr>
            <th scope="row" className="px-4 py-2.5 font-normal text-ink-soft sm:px-5">Applications a month</th>
            {plans.map((plan) => (
              <td key={plan.key} className={cn('num px-2 py-2.5 text-center font-medium text-ink', plan.key === RECOMMENDED_PLAN && 'bg-brand-soft/50')}>
                {formatCount(plan.monthlyApplications)}
              </td>
            ))}
          </tr>
          {labels.map((label) => (
            <tr key={label.key}>
              <th scope="row" className="px-4 py-2.5 font-normal text-ink-soft sm:px-5">{label.label}</th>
              {plans.map((plan, planIndex) => {
                const row = rows[planIndex]?.[label.index]
                const coverText =
                  label.key === 'coverLetter' && row?.on ? (plan.features.coverLetterTailoring ? 'Per job' : 'Standard') : null
                return (
                  <td key={plan.key} className={cn('px-2 py-2.5 text-center', plan.key === RECOMMENDED_PLAN && 'bg-brand-soft/50')}>
                    {coverText ? (
                      <span className="text-[12.5px] font-medium text-ink-soft">{coverText}</span>
                    ) : row?.on ? (
                      <CheckIcon className="mx-auto h-4 w-4 text-success" />
                    ) : (
                      <CrossIcon className="mx-auto h-4 w-4 text-ink-faint" />
                    )}
                    <span className="sr-only">{row?.on ? 'Included' : 'Not included'}</span>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
